import { LeftHandFinger, HandPart } from './types';
import { BASE64_ASSETS } from './src/generated/base64Assets';

// Image URLs - These point to the actual PNG files in the assets folder
export const IMAGE_URLS = {
  KEYBOARD_BASE: BASE64_ASSETS.Keyboard_Base,
  LEFT_PALM_INACTIVE: BASE64_ASSETS.Left_palm_inactive,
  LEFT_PALM_ACTIVE: BASE64_ASSETS.Left_palm_active,
  LEFT_PINKY_ACTIVE: BASE64_ASSETS.Left_pinky_active,
  LEFT_RING_ACTIVE: BASE64_ASSETS.Left_ring_active,
  LEFT_MIDDLE_ACTIVE: BASE64_ASSETS.Left_middle_active,
  LEFT_INDEX_ACTIVE: BASE64_ASSETS.Left_index_active,
  LEFT_THUMB_ACTIVE: BASE64_ASSETS.Left_thumb_active,
  KEYS: {
    Q: BASE64_ASSETS.Q,
    A: BASE64_ASSETS.A,
    Z: BASE64_ASSETS.Z,
    W: BASE64_ASSETS.W,
    S: BASE64_ASSETS.S,
    X: BASE64_ASSETS.X,
    E: BASE64_ASSETS.E,
    D: BASE64_ASSETS.D,
    C: BASE64_ASSETS.C,
    R: BASE64_ASSETS.R,
    F: BASE64_ASSETS.F,
    V: BASE64_ASSETS.V,
    T: BASE64_ASSETS.T,
    G: BASE64_ASSETS.G,
    SPACE: BASE64_ASSETS.Space,
  },
};

// Use the imported ALL_LEFT_FINGERS from types
// This ensures compatibility with KeyboardHero's FingerType system

// ============================================================================
// FINGER-KEY MAPPING - COMPATIBLE WITH KEYBOARDHERO SYSTEM
// ============================================================================
// Import the exact finger-key mapping from KeyboardHero's fingerKeyMapping.ts
// This ensures 100% compatibility with existing logic
export const FINGER_KEY_MAP: Record<LeftHandFinger, string[]> = {
  'leftPinky': ['A', 'Q', 'W'],              // Exact mapping from KeyboardHero
  'leftRing': ['E', 'S'],                    // Exact mapping from KeyboardHero  
  'leftMiddle': ['D', 'R', 'T'],             // Exact mapping from KeyboardHero
  'leftPointing': ['G', 'F', 'V'],      // Exact mapping from KeyboardHero (leftPointing)
  'leftThumb': ['SPACE', 'Z', 'X', 'C'],           // Exact mapping from KeyboardHero + Z, X keys
};

// Create a reverse map for efficient lookup
export const KEY_FINGER_MAP: Record<string, LeftHandFinger> = {};
Object.entries(FINGER_KEY_MAP).forEach(([finger, keys]) => {
  keys.forEach(key => {
    KEY_FINGER_MAP[key] = finger as LeftHandFinger;
  });
});

// Logging to prove compatibility with existing system
console.log('🔗 LeftHandPicker: Using KeyboardHero-compatible finger-key mapping:', FINGER_KEY_MAP);


// ============================================================================
// SVG FINGER DETECTION SYSTEM
// ============================================================================
// 
// This system uses precise SVG paths extracted from Adobe Illustrator files
// combined with positioning adjustments to match the exact finger positions
// in the PNG hand images.
//
// MANUAL FINE-TUNING INSTRUCTIONS:
// ================================
// If finger hover areas don't perfectly align with visual fingers:
// 1. Set DEBUG_FINGER_AREAS = true below to visualize areas
// 2. Adjust the values in FINGER_POSITION_ADJUSTMENTS below
// 3. Use translateX/Y to move the entire finger area
// 4. Use scaleX/Y to resize the area if needed
// 5. Positive translateX moves RIGHT, positive translateY moves DOWN
// 6. Test each finger individually by hovering over them
// 7. Set DEBUG_FINGER_AREAS = false when alignment is perfect
//
// EXAMPLE: If the pinky finger area is too far left, increase translateX
// EXAMPLE: If the index finger area is too low, decrease translateY
// ============================================================================

// DEBUG MODE: Set to true to see finger areas visually (red semi-transparent overlay)
export const DEBUG_FINGER_AREAS = false; // <-- CHANGE THIS TO true FOR DEBUGGING

// STEP 1: POSITIONING ADJUSTMENTS - MODIFY THESE VALUES TO FINE-TUNE ALIGNMENT
export const FINGER_POSITION_ADJUSTMENTS: Record<HandPart, {
  translateX: number;
  translateY: number;
  scaleX?: number;
  scaleY?: number;
}> = {
  // PINKY FINGER ADJUSTMENT - Modify these values to align pinky area
  [HandPart.PINKY]: {
    translateX: 0,    // Move LEFT (-) or RIGHT (+)
    translateY: 0,    // Move UP (-) or DOWN (+)
    scaleX: 1,        // Make narrower (0.9) or wider (1.1)
    scaleY: 1,        // Make shorter (0.9) or taller (1.1)
  },

  // RING FINGER ADJUSTMENT - Modify these values to align ring finger area
  [HandPart.RING]: {
    translateX: 0,
    translateY: 0,
    scaleX: 1,
    scaleY: 1,
  },

  // MIDDLE FINGER ADJUSTMENT - Modify these values to align middle finger area
  [HandPart.MIDDLE]: {
    translateX: 0,
    translateY: 0,
    scaleX: 1,
    scaleY: 1,
  },

  // INDEX FINGER ADJUSTMENT - Modify these values to align index finger area
  [HandPart.INDEX]: {
    translateX: 0,
    translateY: 0,
    scaleX: 1,
    scaleY: 1,
  },

  // THUMB ADJUSTMENT - Modify these values to align thumb area
  [HandPart.THUMB]: {
    translateX: 0,
    translateY: 0,
    scaleX: 1,
    scaleY: 1,
  },

  // PALM ADJUSTMENT - Modify these values to align palm area
  [HandPart.PALM]: {
    translateX: 0,
    translateY: 0,
    scaleX: 1,
    scaleY: 1,
  },
};

// STEP 2: ACCURATE SVG PATHS - These are extracted from the actual Adobe Illustrator SVG files
// DO NOT MODIFY THESE - They represent the exact finger shapes
export const HAND_PART_SVG_PATHS: Record<HandPart, string> = {
  // PINKY - Extracted from Left_pinky_svg.svg
  [HandPart.PINKY]: "M448.97,488.25c-3.02-21.59-5.96-42.65-8.9-63.71c-2.03-14.5-4.34-28.98-5.95-43.53c-0.59-5.31-0.47-11.05,0.9-16.18c9.06-33.93,17.79-67.98,28.24-101.49c4.6-14.76,4.27-29.91,7.64-44.62c1.6-6.96,2.52-14.08,4.14-21.04c1.93-8.29,8.26-10.57,16.47-6.67c12.49,5.93,18.69,16.53,22.26,29.17c5.02,17.75,2.74,35.38-1.55,52.8c-6.52,26.49-13.49,52.86-20.26,79.29c-1.32,5.15-2.73,10.29-3.8,15.5c-0.34,1.68-0.09,3.69,0.51,5.32c7.2,19.68,14.41,39.37,21.87,58.95c1.83,4.81,1.74,7.87-3.09,11.18c-14.15,9.68-27.98,19.85-41.68,30.16C460.08,477.66,455.06,482.81,448.97,488.25z",

  // RING - Extracted from Left_ring_svg-01.svg
  [HandPart.RING]: "M546.38,441.03c-7.15,0.36-14.3,0.75-21.45,1.07c-1.48,0.06-3.07,0.14-4.43-0.3c-5.6-1.82-6.42-3.38-5.06-9.39c8.29-36.5,16.6-72.99,24.89-109.48c1.4-6.16,3.17-12.28,3.99-18.53c2.12-16.25,12.16-28.59,20.21-41.86c24.3-40.11,48.65-80.18,73.46-119.97c8.44-13.53,12.46-29.04,20.76-42.57c6.67-10.88,12.66-22.18,19.26-33.11c3.28-5.43,12.22-4.96,15.99,0.51c5.61,8.16,8.37,17.31,9.13,27.1c1.95,25.27-4.31,48.95-14.91,71.5c-6.93,14.73-14.98,28.92-22.71,43.26c-15.93,29.56-31.7,59.22-48.19,88.48c-4.27,7.57-6.55,15.13-7.92,23.57c-2.44,15.06-4.81,30.31-9.35,44.81c-7.43,23.74-16.53,46.97-25.14,70.33c-0.55,1.49-2.92,3.12-4.58,3.27C562.36,440.42,554.36,440.64,546.38,441.03z",

  // MIDDLE - Extracted from Left_middle_svg.svg
  [HandPart.MIDDLE]: "M825.91,93.6c-0.18,18.03-6.12,38.49-16.61,57.26c-11.34,20.28-23.78,39.98-36.5,59.44c-16.38,25.07-33.52,49.64-50.32,74.44c-7.93,11.71-15.33,23.82-23.88,35.05c-4.97,6.52-7.86,13.67-10.72,21.06c-6.24,16.13-12.84,32.11-18.91,48.3c-7.79,20.8-16.29,41.44-22.23,62.78c-2.37,8.5-6.19,15.3-10.83,22.13c-0.8,1.17-3.22,2.16-4.61,1.87c-22.86-4.73-40.03-18.79-55.98-34.72c-0.85-0.85-1.15-3.07-0.71-4.28c7.37-19.97,13.05-40.85,22.84-59.58c13.44-25.72,21.15-53.47,32.72-79.75c3.48-7.9,9.22-14.92,14.54-21.87c24.59-32.1,49.68-63.84,74.02-96.13c11.41-15.13,21.65-31.15,32.23-46.89c13.7-20.37,27.12-40.94,40.89-61.26c2.77-4.08,6.3-7.78,10-11.07c5.87-5.23,11.25-4.51,16.38,1.55C823.22,67.83,825.89,77.54,825.91,93.6z",

  // INDEX - Extracted from Left_index_svg-01.svg
  [HandPart.INDEX]: "M696.1,513.11c-19.77-15.7-39.02-30.99-58.51-46.47c1.89-3.37,3.28-8.18,6.44-11c8-7.15,12.89-16.45,18.28-25.22c12.09-19.67,23.27-39.9,34.68-59.97c2.7-4.76,4.75-9.89,7.34-14.72c1.08-2.02,2.52-4.03,4.26-5.49c8.92-7.48,18.27-14.47,26.96-22.2c28-24.9,56.01-49.8,83.5-75.25c10.08-9.33,18.42-20.51,28.15-30.26c12.44-12.47,25.37-24.47,38.34-36.39c4.22-3.87,6.79-3.39,10.82,0.58c6.57,6.46,9.26,14.14,8.54,23.49c-1.44,18.64-8.58,34.98-18.52,50.47c-17.73,27.61-41.42,49.85-64.67,72.53c-18.72,18.25-37.69,36.27-55.62,55.28c-5.84,6.19-8.78,15.14-12.98,22.86c-15.13,27.74-30.16,55.54-45.4,83.21C704.2,500.94,699.99,506.94,696.1,513.11z",

  // THUMB - Extracted from Left_thumb_svg-01.svg
  [HandPart.THUMB]: "M886.06,542.83c-9.91,7.3-19.8,14.02-29.02,21.55c-9.79,7.99-19.02,16.67-28.3,25.26c-1.75,1.62-2.9,4.08-3.8,6.37c-6.04,15.16-16.29,27.81-28.88,37.02c-29.59,21.65-54.2,48.14-78.63,75c-6.39,7.02-13.07,13.79-19.23,21c-3.01,3.52-6.08,3.91-10.09,2.58c-18.17-5.98-32.26-16.69-40.95-34.12c-5.54-11.12-13.14-21.16-16.05-33.63c-2.32-9.95,0.66-18.32,5.95-26.32c0.57-0.87,1.79-1.53,2.84-1.82c8.92-2.45,17.75-5.94,26.85-6.96c26.82-3,49.29-14.57,68.36-33c12.92-12.49,25.09-25.74,37.58-38.68c12.68-13.15,28.67-20.51,45.54-26.54c13.76-4.92,27.61-5.39,41.78-2.44C870.44,530.25,878.98,535.51,886.06,542.83z",

  // PALM - Extracted from Left_palm_svg.svg
  [HandPart.PALM]: "M514.89,439.08c4.72,0.69,9.46,2.06,14.17,1.95c13.65-0.33,27.28-1.41,40.93-1.8c2.45-0.07,5.34,1.2,7.34,2.75c4.6,3.57,8.93,7.51,13.07,11.61c12.3,12.14,27.8,17.84,44.31,22.32c1.77-2.35,3.64-4.83,5.72-7.6c4.87,3.33,9.74,6.23,14.12,9.74c13.01,10.4,25.8,21.08,38.77,31.54c2.19,1.77,2.97,3.27,2.24,6.28c-9.09,37.51-17.98,75.07-26.75,111.89c-8.39,2.4-16.19,4.47-23.88,6.87c-8.13,2.53-13.43,10.09-14.48,18.65c-0.93,7.65,0.79,15,4.07,21.3c7.11,13.67,13.69,27.73,24.03,39.59c5.14,5.9,11.32,9.69,18.12,12.82c5.24,2.42,10.7,4.36,14.16,5.75c-12.48,13.2-25.3,27.44-38.86,40.93c-25.27,25.14-50.95,49.89-76.58,74.66c-2.14,2.07-5.1,3.71-7.97,4.56c-13.24,3.92-26.63,7.35-39.86,11.3c-2.23,0.66-4.79,2.59-5.77,4.62c-6.06,12.56-12.9,24.94-17.15,38.13c-3.73,11.58-4.55,24.11-6.41,36.26c-4.96,32.4-10.01,64.78-14.61,97.23c-3.19,22.57-5.27,45.29-8.67,67.82c-1.73,11.43-5.29,22.57-8.15,34.32c-10.66-1.62-20.65-3.09-30.62-4.66c-39.46-6.22-78.93-12.42-118.37-18.81c-2.56-0.41-5.4-1.82-7.22-3.65c-33.23-33.46-66.3-67.08-99.5-100.56c-2.53-2.56-2.43-4.25-0.6-7.19c5.6-9.01,10.75-18.29,16.1-27.45c4.68-8.02,9.38-16.03,14.08-24.03c2.1-3.57,3.94-7.34,6.4-10.65c4.74-6.4,10.76-12.04,14.53-18.93c4.8-8.79,8.13-18.42,11.63-27.86c3.56-9.64,6.59-19.47,9.78-29.24c3.09-9.46,6.05-18.95,9.14-28.41c2.52-7.72,4.92-15.5,7.88-23.06c0.97-2.48,3.13-5.01,5.43-6.35c8.52-4.95,11.26-14.11,16.57-21.4c10.32-14.16,13.94-31.44,22.19-46.51c5.16-9.43,11.12-18.43,16.82-27.57c5.44-8.72,11.01-17.36,16.51-26.04c5.06-7.98,9.06-16.96,15.46-23.66c6.53-6.84,7.69-14.97,10.14-23.03c2.89-9.51,5.91-18.98,8.75-28.5c3.17-10.64,6.15-21.33,9.29-31.97c2.81-9.53,5.73-19.03,8.57-28.56c3.17-10.64,6.29-21.29,9.5-31.91c0.57-1.87,1.65-3.58,2.28-5.44c1.85-5.43,4.56-10.8,5.16-16.38c1.25-11.64,7.9-19.33,16.62-25.78c14.99-11.1,30.13-22,45.23-32.94c2.02-1.46,4.21-2.68,6.33-4.01C514.87,438.36,514.88,438.72,514.89,439.08z",
};

// ============================================================================
// LETTER POSITIONING & FONT SYSTEM
// ============================================================================
//
// MANUAL FINE-TUNING INSTRUCTIONS FOR LETTERS:
// ============================================
// 1. Set DEBUG_LETTER_POSITIONS = true below to see letter positions
// 2. Adjust values in LETTER_POSITION_ADJUSTMENTS for each key
// 3. Modify LETTER_FONT_SETTINGS for font appearance
// 4. Use percentage values (e.g., '50%' = center, '25%' = quarter from edge)
// 5. Test each letter individually
// 6. Set DEBUG_LETTER_POSITIONS = false when positioning is perfect
//
// COORDINATE SYSTEM:
// - 0% = Left/Top edge
// - 50% = Center
// - 100% = Right/Bottom edge
// ============================================================================

// DEBUG MODE: Set to true to see letter positioning guides
export const DEBUG_LETTER_POSITIONS = false; // <-- CHANGE THIS TO true FOR DEBUGGING

// FONT SETTINGS - CUSTOMIZE LETTER APPEARANCE
export const LETTER_FONT_SETTINGS = {
  fontSize: '22px',        // Size of letters (e.g., '24px', '1.5rem', '2vw')
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontWeight: 'bold',      // 'normal', 'bold', '600', '700', etc.
  color: '#000000ff',        // Letter color (hex, rgb, or CSS color names)
  textShadow: '0px 0px 0px rgba(0,0,0,0.5)', // Shadow for better visibility
  letterSpacing: '0px',    // Space between characters
};

// LETTER POSITION ADJUSTMENTS - FINE-TUNE EACH LETTER POSITION
export const LETTER_POSITION_ADJUSTMENTS: Record<string, {
  top: string;     // Vertical position (%, px, em, etc.)
  left: string;    // Horizontal position (%, px, em, etc.)
  offsetX?: string; // Additional horizontal offset
  offsetY?: string; // Additional vertical offset
}> = {
  // ROW 1 KEYS (Top row)
  Q: { top: '15.6%', left: '40%' },
  W: { top: '15.6%', left: '52%' },
  E: { top: '15.6%', left: '64%' },
  R: { top: '15.6%', left: '76%' },
  T: { top: '15.6%', left: '88%' },

  // ROW 2 KEYS (Middle row)
  A: { top: '25.5%', left: '45.5%' },
  S: { top: '25.5%', left: '57.5%' },
  D: { top: '25.5%', left: '70%' },
  F: { top: '25.5%', left: '82%' },
  G: { top: '25.5%', left: '93%' },

  // ROW 3 KEYS (Bottom row)
  Z: { top: '35.6%', left: '50%' },
  X: { top: '35.6%', left: '61%' },
  C: { top: '35.6%', left: '73%' },
  V: { top: '35.6%', left: '84.5%' },

  // SPACEBAR
  SPACE: { top: '45.5%', left: '70%' },
};

// LEGACY SUPPORT - This will be generated from LETTER_POSITION_ADJUSTMENTS
export const KEY_LETTER_POSITIONS: Record<string, { top: string; left: string }> =
  Object.fromEntries(
    Object.entries(LETTER_POSITION_ADJUSTMENTS).map(([key, pos]) => [
      key,
      { top: pos.top, left: pos.left }
    ])
  );